import { useMemo } from "react";

// Render a plain string with {{NAME}} tokens swapped for inline chips.
// Used in read-only displays (tab titles, modal headers, list rows) so
// the UI matches what the user types in ChipInput / CodeMirror.
//
// Tokens are matched against the active environment's vars (passed via
// `vars`) so missing references can be flagged with a subtle warning
// style — same vocabulary as cmVarHighlight.
const TOKEN = /\{\{\s*([A-Za-z_][A-Za-z0-9_.-]*)\s*\}\}/g;

// Open the env modal at a specific variable. Layout exposes the helper
// on `window.__openEnvs(name?)` to avoid prop-drilling through every
// builder.
function openEnvsAt(name: string): void {
  const fn = (window as unknown as { __openEnvs?: (name?: string) => void }).__openEnvs;
  if (fn) fn(name);
}

export default function VarText({
  value,
  vars,
  className = "",
  title,
}: {
  value: string;
  vars?: Record<string, string | undefined> | null;
  className?: string;
  title?: string;
}) {
  const segments = useMemo(() => {
    const out: Array<{ kind: "text"; value: string } | { kind: "chip"; name: string; resolved: boolean }> = [];
    let last = 0;
    for (const m of value.matchAll(TOKEN)) {
      if (m.index! > last) out.push({ kind: "text", value: value.slice(last, m.index!) });
      const name = m[1];
      const resolved = !vars ? true : Object.prototype.hasOwnProperty.call(vars, name) && (vars[name] ?? "") !== "";
      out.push({ kind: "chip", name, resolved });
      last = m.index! + m[0].length;
    }
    if (last < value.length) out.push({ kind: "text", value: value.slice(last) });
    return out;
  }, [value, vars]);

  return (
    <span className={className} title={title ?? value}>
      {segments.map((s, i) =>
        s.kind === "text" ? (
          <span key={i}>{s.value}</span>
        ) : (
          <button
            key={i}
            type="button"
            onClick={(e) => { e.stopPropagation(); openEnvsAt(s.name); }}
            className={
              "var-chip inline-flex items-center mx-0.5 px-1 py-[1px] rounded text-[11px] font-medium font-mono align-baseline cursor-pointer hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft " +
              (s.resolved
                ? "bg-accent-soft text-accent-bright border border-[color-mix(in_srgb,var(--accent)_28%,transparent)]"
                : "bg-warn/15 text-warn border border-warn/40")
            }
            title={s.resolved ? `Open ${s.name} in environments` : `Define ${s.name} in environments`}
          >{s.name}</button>
        )
      )}
    </span>
  );
}
